import { useState } from "react";
import {
  ArrowLeft,
  User,
  Lock,
  Check,
  Loader2,
  AlertTriangle,
  Sparkles,
  Eye,
  EyeOff,
} from "lucide-react";
import { apiv2 } from "../apiv2";
import { useAuth, setUser } from "../auth";
import GreenTendrils from "./GreenTendrils";

interface Props {
  onBack: () => void;
}

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (v: string) => v.length >= 8 },
  { label: "One uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
  { label: "One lowercase letter", test: (v: string) => /[a-z]/.test(v) },
  { label: "One digit", test: (v: string) => /\d/.test(v) },
  {
    label: "One special character",
    test: (v: string) => /[!@#$%^&*(),.?":{}|<>\-_=+[\]\\;'/`~]/.test(v),
  },
];

export default function ProfileSettings({ onBack }: Props) {
  const { user } = useAuth();

  // Name form
  const [fullName, setFullName] = useState(user?.full_name ?? "");
  const [savingName, setSavingName] = useState(false);
  const [nameSuccess, setNameSuccess] = useState("");

  // Password form
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const passwordValid = PASSWORD_RULES.every((r) => r.test(newPassword));
  const passwordsMatch =
    newPassword === confirmPassword && newPassword.length > 0;

  const handleSaveName = async () => {
    if (!fullName.trim()) return;
    setSavingName(true);
    setError(null);
    setNameSuccess("");
    try {
      const updated = await apiv2.updateUser(user.id, {
        full_name: fullName.trim(),
      });
      setUser({ ...user, full_name: updated.full_name });
      setNameSuccess("Name updated successfully");
      setTimeout(() => setNameSuccess(""), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update name");
    } finally {
      setSavingName(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordValid || !passwordsMatch) return;
    setSavingPassword(true);
    setError(null);
    setPasswordSuccess("");
    try {
      const updated = await apiv2.updateUser(user.id, {
        password: newPassword,
      });
      setUser({ ...user, must_change_password: updated.must_change_password });
      setNewPassword("");
      setConfirmPassword("");
      setPasswordSuccess("Password changed successfully");
      setTimeout(() => setPasswordSuccess(""), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to change password",
      );
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="overflow-y-auto custom-scrollbar h-full relative bg-surface-500">
      <div className="absolute inset-0 dot-grid" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <GreenTendrils />

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-10 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-5 animate-fade-in">
          <button
            onClick={onBack}
            className="p-2.5 rounded-xl border border-white/8 hover:border-brand-500/30 transition-all duration-300 hover:bg-brand-500/5"
            style={{ background: "rgba(28,28,42,0.5)" }}
          >
            <ArrowLeft size={18} className="text-white/50" />
          </button>
          <div>
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-500/30 text-brand-400 text-xs font-medium mb-3"
              style={{ background: "rgba(134,188,37,0.08)" }}
            >
              <Sparkles size={12} />
              My Account
            </div>
            <h1 className="text-4xl font-black tracking-tight">
              <span className="gradient-text-green">Profile Settings</span>
            </h1>
            <p className="text-sm text-white/40 mt-1">{user.email}</p>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300 flex items-center gap-2">
            <AlertTriangle size={16} className="flex-shrink-0" />
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-300 text-xs ml-auto"
            >
              dismiss
            </button>
          </div>
        )}

        {/* ── Update Name ──────────────────────────────── */}
        <section
          className="glass rounded-2xl p-6 space-y-4 animate-fade-in"
          style={{
            background: "rgba(28,28,42,0.6)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <User size={14} className="text-brand-500" /> Display Name
          </h3>
          <div>
            <label className="block text-xs text-white/40 mb-1">
              Full Name
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSaveName}
              disabled={
                savingName ||
                !fullName.trim() ||
                fullName.trim() === user.full_name
              }
              className="px-4 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 flex items-center gap-2"
              style={{
                background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
              }}
            >
              {savingName ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Check size={14} />
              )}
              Save Name
            </button>
            {nameSuccess && (
              <span className="text-xs text-green-400">{nameSuccess}</span>
            )}
          </div>
        </section>

        {/* ── Change Password ──────────────────────────── */}
        <section
          className="glass rounded-2xl p-6 space-y-4 animate-fade-in"
          style={{
            background: "rgba(28,28,42,0.6)",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Lock size={14} className="text-brand-500" /> Change Password
          </h3>

          <div>
            <label className="block text-xs text-white/40 mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
                className="w-full px-3 py-2 pr-10 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Password strength rules */}
          {newPassword.length > 0 && (
            <div className="grid grid-cols-2 gap-1">
              {PASSWORD_RULES.map((rule) => (
                <div
                  key={rule.label}
                  className={`text-[11px] flex items-center gap-1 ${rule.test(newPassword) ? "text-green-400" : "text-white/30"}`}
                >
                  <Check
                    size={10}
                    className={
                      rule.test(newPassword)
                        ? "text-green-400"
                        : "text-white/15"
                    }
                  />
                  {rule.label}
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="block text-xs text-white/40 mb-1">
              Confirm Password
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat your new password"
              className={`w-full px-3 py-2 bg-surface-300/50 border rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 ${
                confirmPassword.length > 0 && !passwordsMatch
                  ? "border-red-500/50"
                  : "border-white/8"
              }`}
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-[11px] text-red-400 mt-1">
                Passwords do not match
              </p>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleChangePassword}
              disabled={savingPassword || !passwordValid || !passwordsMatch}
              className="px-4 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50 flex items-center gap-2"
              style={{
                background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
              }}
            >
              {savingPassword ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Lock size={14} />
              )}
              Change Password
            </button>
            {passwordSuccess && (
              <span className="text-xs text-green-400">{passwordSuccess}</span>
            )}
          </div>
        </section>

        {/* Account info (read-only) */}
        <section
          className="glass rounded-2xl p-6 space-y-3 animate-fade-in"
          style={{
            background: "rgba(28,28,42,0.4)",
            border: "1px solid rgba(255,255,255,0.04)",
          }}
        >
          <h3 className="text-sm font-bold text-white/50">Account Info</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="text-[11px] text-white/30">Role</span>
              <p className="text-xs text-white/70 capitalize">
                {user.role.replace("_", " ")}
              </p>
            </div>
            <div>
              <span className="text-[11px] text-white/30">Status</span>
              <p className="text-xs text-white/70">
                {user.is_active ? "Active" : "Inactive"}
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
